import React, { useState, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useHistory, Link, useLocation } from "react-router-dom";
import { AuthService } from "../services/AuthService";
import { DriverService } from "../services/DriverService";
import { setSavedState } from "../utils/LocalStorage.utils";
import LSVariables from "../configs/LS_variables.json";
import logo from "../assets/logo-prodrive.svg";
import driverLoginBg from "../assets/login-bg.svg";

const customDecode = (str: string): string => {
  // Replace spaces with '+' to restore the original encoding
  const restoredPlus = str.replace(/ /g, '+');
  // Decode the URL components
  const decoded = decodeURIComponent(restoredPlus);
  return decoded;
};

const Login: React.FC = () => {
  const [isLoadingLogin, setIsLoginLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const location = useLocation();
  const authService = new AuthService();
  const driverService = new DriverService();

  const emailFromURL = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const encodedEmail = params.get("email");
    if (encodedEmail) {
      const decodedEmail = customDecode(encodedEmail);
      return decodedEmail;
    }
    return "";
  }, [location.search]);

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Veuillez entrer une adresse e-mail valide")
      .required("L'e-mail est requis"),
    password: yup.string().required("Le mot de passe est requis"),
    accept: yup
      .boolean()
      .oneOf([true], "Vous devez accepter les termes et conditions"),
  });

  const formik = useFormik({
    initialValues: {
      email: emailFromURL,
      password: "",
      accept: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoginLoading(true);
      try {
        const res = await authService.logIn({ ...values, token: "" });
        if (res && res.data) {
          const driverData = res.data.data;
          setSavedState(driverData, LSVariables.saved_user);

          // Check if the driver account is active and not expired
          if (!driverService.isDriverActive(driverData)) {
            setError(
              "Votre compte est inactif ou a expiré. Veuillez contacter votre administrateur."
            );
            return;
          }

          history.push("/introduction");
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError("Email ou mot de passe incorrect");
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError("Une erreur s'est produite lors de la connexion");
        }
      } finally {
        setIsLoginLoading(false);
      }
    },
  });

  return (
    <div className="flex h-screen">
      <div className="md:flex md:w-1/2 overflow-clip  bg-slate-50">
        <img src={driverLoginBg} alt="" className="object-cover scale-125" />
      </div>

      {/* Right side - Login Form */}
      <div className="md:w-1/2 mx-auto md:m flex items-center justify-center">
        <div className="w-full max-w-[500px] ">
          <div className="py-12">
            <div className="text-center mb-8">
              <img
                src={logo}
                alt="ProDrive Logo"
                className="mx-auto h-12 mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-800">
                Connexion
              </h2>
            </div>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div>
                <input
                  id="email"
                  type="email"
                  placeholder="E-mail"
                  autoComplete="email"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("email")}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div>
                <input
                  id="password"
                  type="password"
                  placeholder="Mot de passe"
                  autoComplete="current-password"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("password")}
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-center">
                <input
                  id="accept"
                  type="checkbox"
                  className="h-4 text-primary focus:ring-primary-light border-gray-300 rounded"
                  {...formik.getFieldProps("accept")}
                />
                <label
                  htmlFor="accept"
                  className="ml-2 block text-sm text-gray-900"
                >
                  J'accepte les{" "}
                  <Link
                    to="/terms"
                    className="text-primary hover:text-primary-light hover:underline"
                  >
                    termes et conditions
                  </Link>
                </label>
              </div>
              {formik.touched.accept && formik.errors.accept && (
                <p className="mt-1 text-sm p-error text-center">
                  {formik.errors.accept}
                </p>
              )}

              {error && <p className="text-sm p-error text-center">{error}</p>}

              <div>
                <button
                  type="submit"
                  disabled={isLoadingLogin}
                  className="w-full py-4 px-4 border border-transparent rounded-2xl shadow-sm text-md font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light transition duration-150 ease-in-out"
                >
                  {isLoadingLogin ? "Connexion en cours..." : "Se connecter"}
                </button>
              </div>
            </form>
          </div>

          {/* Added interface chauffeur text and copyright */}
          <div className="text-center mt-8">
            <p className="text-lg font-semibold text-gray-700">
              Interface chauffeur
            </p>
            <p className="text-sm text-gray-500 mt-2">
              &copy; {new Date().getFullYear()} master d. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
